import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ProductCard } from '../components/custom/ProductCard';
import { FilterBottomSheet } from '../components/custom/FilterBottomSheet';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Baby, User, Users, ArrowLeft, X } from 'lucide-react';

function AgeShopPage() {
  const { ageGroup } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});

  const ageInfo = {
    '0-2': {
      title: '0-2 Years',
      subtitle: 'Perfect for Newborns & Toddlers',
      icon: Baby,
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-50'
    },
    '3-5': {
      title: '3-5 Years',
      subtitle: 'Great for Preschoolers',
      icon: User,
      color: 'from-cyan-500 to-cyan-600',
      bgColor: 'bg-cyan-50'
    },
    '6-12': {
      title: '6-12 Years',
      subtitle: 'Ideal for School Age',
      icon: Users,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50'
    }
  };

  const currentAgeInfo = ageInfo[ageGroup] || ageInfo['3-5'];
  const Icon = currentAgeInfo.icon;

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('age_group', ageGroup);
        
        if (filters.categories && filters.categories.length > 0) {
          filters.categories.forEach(cat => params.append('category', cat));
        }
        if (filters.priceRange) {
          params.append('min_price', filters.priceRange[0]);
          params.append('max_price', filters.priceRange[1]);
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/products?${params.toString()}`
        );
        const data = await response.json();
        setProducts(data.products || []);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [ageGroup, filters]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const removeFilter = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: Array.isArray(prev[filterType])
        ? prev[filterType].filter(v => v !== value)
        : filterType === 'priceRange' ? [0, 3000] : prev[filterType]
    }));
  };

  const activeFilterChips = [];
  if (filters.categories) {
    filters.categories.forEach(cat => {
      activeFilterChips.push({ type: 'categories', value: cat, label: cat });
    });
  }
  if (filters.ageGroups) {
    filters.ageGroups.forEach(age => {
      activeFilterChips.push({ type: 'ageGroups', value: age, label: `Age: ${age}` });
    });
  }
  if (filters.features) {
    filters.features.forEach(feat => {
      activeFilterChips.push({ type: 'features', value: feat, label: feat.replace('_', ' ') });
    });
  }

  return (
    <div className="min-h-screen pb-20 md:pb-8">
      <Helmet>
        <title>{`Shop for ${currentAgeInfo.title} | Mothering`}</title>
        <meta name="description" content={`Quality products ${currentAgeInfo.subtitle} - Free delivery on all orders`} />
      </Helmet>

      <section className={`bg-gradient-to-r ${currentAgeInfo.color} text-white py-8 px-4`}>
        <div className="container mx-auto max-w-6xl">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Icon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{currentAgeInfo.title}</h1>
              <p className="text-white/90 text-sm md:text-base">{currentAgeInfo.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-6xl px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <FilterBottomSheet 
              onApplyFilters={handleApplyFilters}
              activeFilters={filters}
            />
            
            {activeFilterChips.map((chip, index) => (
              <Badge
                key={`${chip.type}-${chip.value}-${index}`}
                variant="secondary"
                className="gap-1"
              >
                {chip.label}
                <X
                  className="w-3 h-3 cursor-pointer"
                  onClick={() => removeFilter(chip.type, chip.value)}
                />
              </Badge>
            ))}
          </div>
          
          <span className="text-sm text-muted-foreground">
            {products.length} products
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-xl" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full ${currentAgeInfo.bgColor} flex items-center justify-center`}>
              <Icon className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No products found</h3>
            <p className="text-muted-foreground mb-4">Try adjusting your filters</p>
            <Button onClick={() => setFilters({})}>
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AgeShopPage;
